// utils/downloadAdvancesCSV.ts

import { UserAdvance, UserAdvanceProgress } from "../../../../../types"
import { calculateContractEnds, monthsBetween } from "../../../../../utility"
import ExcelJS from "exceljs"
import { saveAs } from "file-saver"

const getEstimatedRecoupment = (userAdvanceProgress: UserAdvanceProgress) => {
  const { advances, royalties, projections, recoupMonthTotal } = userAdvanceProgress

  const advanceTracker: { [key: string]: boolean } = {}
  const advanceRecoupTracker: { [key: string]: string } = {}
  const advanceRemainingTracker: { [key: string]: number } = {}
  const advanceEstimateRecoupTracker: { [key: string]: string } = {}
  const advanceIndividualAmountTracker: { [key: string]: number } = {} // Track each advance's original amount

  let advanceTotal = 0
  let royaltyTotal = 0
  Object.entries(royalties).forEach(([royaltyMonth, royaltyAmount]) => {
    royaltyTotal += royaltyAmount
  })

  // Sort advances by month to process them in chronological order
  const sortedAdvances = Object.entries(advances).sort(([a], [b]) => a.localeCompare(b))
  const months = Object.keys(royalties).sort()

  // Track cumulative advances - royalties "pay off" advances in order (FIFO)
  let cumulativeAdvances = 0
  let previousAdvancesCumulative = 0

  sortedAdvances.forEach(([advMonth, advAmount]) => {
    advanceTotal += advAmount
    advanceIndividualAmountTracker[advMonth] = advAmount
    previousAdvancesCumulative = cumulativeAdvances
    cumulativeAdvances += advAmount

    let cumulativeRoyalty = 0
    let recouped = false

    for (let i = 0; i < months.length; i++) {
      const month = months[i]
      cumulativeRoyalty += royalties[month] || 0

      // Check if cumulative royalties >= cumulative advances (including this advance)
      if (cumulativeRoyalty >= cumulativeAdvances) {
        advanceTracker[advMonth] = true // advance recouped
        advanceRecoupTracker[advMonth] = month
        advanceRemainingTracker[advMonth] = 0
        advanceEstimateRecoupTracker[advMonth] = "0"
        recouped = true
        break
      }
    }

    if (!recouped) {
      advanceTracker[advMonth] = false
      advanceRecoupTracker[advMonth] = months[months.length - 1] || advMonth

      // Calculate remaining for THIS specific advance only
      // Royalties first pay off previous advances, then this advance
      const royaltiesAvailableForThisAdvance = Math.max(0, cumulativeRoyalty - previousAdvancesCumulative)
      const thisAdvanceRemaining = Math.max(0, advAmount - royaltiesAvailableForThisAdvance)
      advanceRemainingTracker[advMonth] = thisAdvanceRemaining

      // Estimate recoup time using projections data from the API
      const projectionMonths = Object.keys(projections).sort()
      let remainingToRecoup = thisAdvanceRemaining
      let monthsToRecoup = 0

      for (const projMonth of projectionMonths) {
        remainingToRecoup -= projections[projMonth]
        monthsToRecoup++
        if (remainingToRecoup <= 0) {
          break
        }
      }

      if (remainingToRecoup <= 0) {
        advanceEstimateRecoupTracker[advMonth] = monthsToRecoup.toString()
      }
    }
  })

  // Track which months are recoup milestones (using cumulative advances logic)
  const recoupedMonthTracker: { [month: string]: boolean } = {}
  let cumulativeAdvancesForRecoup = 0
  sortedAdvances.forEach(([advMonth, advAmount]) => {
    cumulativeAdvancesForRecoup += advAmount
    let cumulativeRoyalty = 0
    for (const month of months) {
      cumulativeRoyalty += royalties[month] || 0
      if (cumulativeRoyalty >= cumulativeAdvancesForRecoup) {
        recoupedMonthTracker[month] = true // mark this month as the one where advance is recouped
        break
      }
    }
  })

  const allMonths = Object.keys({ ...advances, ...royalties, ...projections }).sort()

  const realMonths = Object.keys({ ...advances, ...royalties }).sort()
  const projectionMonths = Object.keys(projections).sort()

  let cumulative = 0
  const realChartData: number[] = []

  realMonths.forEach((month) => {
    const royalty = royalties[month] || 0
    const advance = advances[month] || 0
    cumulative += royalty - advance
    realChartData.push(cumulative)
  })

  const projectionChartData: number[] = []

  projectionMonths.forEach((month) => {
    const projValue = projections[month] || 0
    cumulative += projValue // continue cumulative forward
    projectionChartData.push(cumulative)
  })

  const allEstimatedRecoups: number[] = []
  const allTotalRecoups: number[] = []

  Object.entries(advances).forEach(([month, amount]) => {
    if (advanceRemainingTracker[month] === 0) {
      allEstimatedRecoups.push(0)
      allTotalRecoups.push(monthsBetween(month, advanceRecoupTracker[month]))
    } else {
      const estimatedRecoup = advanceEstimateRecoupTracker[month] ? parseInt(advanceEstimateRecoupTracker[month]) : 0
      if ((estimatedRecoup || 99999) > recoupMonthTotal) {
        // Do nothing
      } else if (advanceEstimateRecoupTracker[month] && parseInt(advanceEstimateRecoupTracker[month]) < 100) {
        const totalRecoup = monthsBetween(month, advanceRecoupTracker[month]) + estimatedRecoup
        allEstimatedRecoups.push(estimatedRecoup)
        allTotalRecoups.push(totalRecoup)
      }
    }
  })

  if (allTotalRecoups.length === 0) {
    return [recoupMonthTotal.toString(), recoupMonthTotal.toString()]
  }

  const meanEstimated = (
    allEstimatedRecoups.reduce((sum, val) => sum + val, 0) / allEstimatedRecoups.length
  ).toLocaleString("en-EN", { maximumFractionDigits: 2 })

  const meanTotal = (allTotalRecoups.reduce((sum, val) => sum + val, 0) / allTotalRecoups.length).toLocaleString(
    "en-EN",
    { maximumFractionDigits: 2 }
  )

  return [meanEstimated, meanTotal || recoupMonthTotal]
}

const calculateTotals = (userAdvanceProgress: UserAdvanceProgress) => {
  const { advances, royalties } = userAdvanceProgress
  let advance_total = 0
  let royalty_total = 0

  Object.entries(advances).forEach(([advMonth, advAmount]) => {
    advance_total += advAmount
  })

  Object.entries(royalties).forEach(([royaltyMonth, royaltyAmount]) => {
    royalty_total += royaltyAmount
  })

  const remaining_total = advance_total - royalty_total
  return [
    advance_total.toLocaleString("en-EN", { maximumFractionDigits: 2, minimumFractionDigits: 2 }),
    royalty_total.toLocaleString("en-EN", { maximumFractionDigits: 2, minimumFractionDigits: 2 }),
    (remaining_total < 0 ? 0 : remaining_total).toLocaleString("en-EN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    })
  ]
}

const pct = (v: number | null) => (v !== null ? (v * 100).toFixed(2) : "")

const initializeBaseRows = () => {
  return {
    user_id: "",
    name: "",
    email: "",
    currency: "",
    estimated_recoup_months: "",
    total_recoup_months: "",
    advance_total: "",
    royalty_total: "",
    remaining_total: "",
    irr_monthly_pct: "",
    irr_annual_pct: "",
    irr_12m_pct: "",
    irr_24m_pct: "",
    irr_36m_pct: ""
  }
}

const getCashflowCell = (
  cashflow: Record<string, number> = {},
  m: string,
  projections: { [key: string]: number },
  isCumulative: boolean
) => {
  return {
    value:
      cashflow[m] || cashflow[m] === 0
        ? cashflow[m].toLocaleString("en-EN", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2
          })
        : "",
    meta: {
      source: isCumulative ? (projections?.[m] ? "projection" : "royalty") : "default"
    }
  }
}

const buildCashflowRows = (
  row: Record<string, any>,
  royalties: { [key: string]: number },
  projections: { [key: string]: number },
  advances: { [key: string]: number },
  timelineActive: boolean,
  cashflowMonths: string[],
  totalMonths: number,
  title: string,
  isCumulative: boolean,
  advanceTotal: number
) => {
  const cashflowMap: Record<string, number> = {}

  // 1. Add royalties (positive)
  Object.entries(royalties ?? {}).forEach(([month, value]) => {
    cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
  })

  // 2. Add projections (positive)
  Object.entries(projections ?? {}).forEach(([month, value]) => {
    cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
  })

  // 3. Add advances (negative)
  Object.entries(advances ?? {}).forEach(([month, value]) => {
    cashflowMap[month] = (cashflowMap[month] || 0) - (value || 0)
  })

  // 4. Convert to sorted array format
  const cashflow: Record<string, number> = {}
  let currentCash = 0

  Object.keys(cashflowMap)
    .sort()
    .forEach((month) => {
      if (isCumulative) {
        currentCash += cashflowMap[month]
        cashflow[month] = currentCash
      } else {
        cashflow[month] = cashflowMap[month]
      }
    })

  row["cashflow"] = title
  // Pivot months → columns

  row["deal_start"] = "-"
  if (title === "Outstanding Advance Balance (EoM)") {
    row["deal_start"] = advanceTotal.toLocaleString("en-EN", { maximumFractionDigits: 2, minimumFractionDigits: 2 })
  } else if (title === "Kahuna Net Cash Flow for IRR") {
    row["deal_start"] = (-1 * advanceTotal).toLocaleString("en-EN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    })
  }

  if (timelineActive) {
    cashflowMonths.forEach((m) => {
      row[`M${m}`] = getCashflowCell(cashflow, m, projections, isCumulative)
    })
  } else {
    let counter = 0
    cashflowMonths.forEach((m) => {
      if (cashflow[m] || cashflow[m] === 0) {
        row[`M${counter}`] = getCashflowCell(cashflow, m, projections, isCumulative)
        counter += 1
      }
    })
    // Fill the rest with blank
    for (let i = counter; i < totalMonths - 1; i++) {
      row[`M${i}`] = {
        value: "",
        meta: { source: isCumulative ? "royalty" : "default" }
      }
    }
  }

  return row
}

const getKahunaBalanceRow = (
  r: UserAdvanceProgress,
  timelineActive: boolean,
  cashflowMonths: string[],
  totalMonths: number
) => {
  const row: Record<string, any> = initializeBaseRows()

  return buildCashflowRows(
    row,
    r.royalties,
    r.projections,
    r.advances,
    timelineActive,
    cashflowMonths,
    totalMonths,
    "Kahuna Balance",
    true,
    0
  )
}

const getActualGrossAndProjectionRow = (
  r: UserAdvanceProgress,
  timelineActive: boolean,
  cashflowMonths: string[],
  totalMonths: number
) => {
  const estimatedAndTotalRecoupment = getEstimatedRecoupment(r)
  const totals = calculateTotals(r)

  const row: Record<string, any> = {
    user_id: r.userId,
    name: `${r.user.firstName} ${r.user.lastName}`.trim(),
    email: r.user.email,
    currency: r.currency,
    estimated_recoup_months: estimatedAndTotalRecoupment[0],
    total_recoup_months: estimatedAndTotalRecoupment[1],
    advance_total: totals[0],
    royalty_total: totals[1],
    remaining_total: totals[2],
    irr_monthly_pct: pct(r.irrMonthly),
    irr_annual_pct: pct(r.irrAnnual),
    irr_12m_pct: pct(r.irr12M),
    irr_24m_pct: pct(r.irr24M),
    irr_36m_pct: pct(r.irr36M)
  }

  return buildCashflowRows(
    row,
    r.actualGrossAndProjection?.royalties,
    r.actualGrossAndProjection?.projections,
    r.actualGrossAndProjection?.advances,
    timelineActive,
    cashflowMonths,
    totalMonths,
    "Gross Collections",
    false,
    0
  )
}

const getArtistBalanceRow = (
  r: UserAdvanceProgress,
  timelineActive: boolean,
  cashflowMonths: string[],
  totalMonths: number,
  advanceTotal: number
) => {
  const row: Record<string, any> = initializeBaseRows()

  return buildCashflowRows(
    row,
    r.artistBalance?.royalties,
    r.artistBalance?.projections,
    r.artistBalance?.advances,
    timelineActive,
    cashflowMonths,
    totalMonths,
    "Outstanding Advance Balance (EoM)",
    false,
    advanceTotal
  )
}

const getArtistPayableRow = (
  r: UserAdvanceProgress,
  timelineActive: boolean,
  cashflowMonths: string[],
  totalMonths: number
) => {
  const row: Record<string, any> = initializeBaseRows()

  return buildCashflowRows(
    row,
    r.artistPayable?.royalties,
    r.artistPayable?.projections,
    r.artistPayable?.advances,
    timelineActive,
    cashflowMonths,
    totalMonths,
    "Artist Payable",
    false,
    0
  )
}

const getNewAdvanceFundedRow = (
  r: UserAdvanceProgress,
  timelineActive: boolean,
  cashflowMonths: string[],
  totalMonths: number
) => {
  const row: Record<string, any> = {
    user_id: `Contract Ends:`,
    name: `${calculateContractEnds(r.dealOffer)}`,
    email: `Type:`,
    currency: `${r.dealOffer?.type || ""}`,
    estimated_recoup_months: `Length:`,
    total_recoup_months: `${r.dealOffer?.contractDuration || ""}`,
    advance_total: `Included In Recoupment:`,
    royalty_total: `${r.dealOffer?.newReleaseInclude || ""}`,
    remaining_total: `Exclusivity:`,
    irr_monthly_pct: `${r.dealOffer?.contractExclusivity || ""}`,
    irr_annual_pct: ``,
    irr_12m_pct: "",
    irr_24m_pct: ``,
    irr_36m_pct: ""
  }

  return buildCashflowRows(
    row,
    r.advancesTimeline,
    {},
    {},
    timelineActive,
    cashflowMonths,
    totalMonths,
    "New Advance Funded",
    false,
    0
  )
}

const getKahunaAdvanceCollectionRow = (
  r: UserAdvanceProgress,
  timelineActive: boolean,
  cashflowMonths: string[],
  totalMonths: number
) => {
  const row: Record<string, any> = {
    user_id: `Admin Fee:`,
    name: `${r.userExpense.adminFee || ""}`,
    email: `New Release Pre-Commission:`,
    currency: `${r.userExpense.distributionFee || ""}`,
    estimated_recoup_months: `New Release Post-Commission:`,
    total_recoup_months: `${r.userExpense.distributionFeePostRecoup || ""}`,
    advance_total: `Takedown Pre-Recoup Commission:`,
    royalty_total: `${r.userExpense.preRecoupCommission || ""}`,
    remaining_total: `Takedown Post-Recoup Commission:`,
    irr_monthly_pct: `${r.userExpense.postRecoupCommission || ""}`,
    irr_annual_pct: `Country:`,
    irr_12m_pct: `${r.userInformation?.country || ""}`,
    irr_24m_pct: `Signed At:`,
    irr_36m_pct: `${r.dealOffer?.signedAt ? r.dealOffer.signedAt.substring(0, 10) : ""}`
  }

  return buildCashflowRows(
    row,
    r.kahunaAdvanceCollection?.royalties,
    r.kahunaAdvanceCollection?.projections,
    r.kahunaAdvanceCollection?.advances,
    timelineActive,
    cashflowMonths,
    totalMonths,
    "Advance Recoup Applied",
    false,
    0
  )
}

const getKahunaRevenueRow = (
  r: UserAdvanceProgress,
  timelineActive: boolean,
  cashflowMonths: string[],
  totalMonths: number
) => {
  const row: Record<string, any> = initializeBaseRows()

  return buildCashflowRows(
    row,
    r.kahunaRevenue?.royalties,
    r.kahunaRevenue?.projections,
    r.kahunaRevenue?.advances,
    timelineActive,
    cashflowMonths,
    totalMonths,
    "Kahuna Revenue",
    false,
    0
  )
}

const getKahunaTotalRow = (
  r: UserAdvanceProgress,
  timelineActive: boolean,
  cashflowMonths: string[],
  totalMonths: number,
  advanceTotal: number
) => {
  const row: Record<string, any> = initializeBaseRows()

  return buildCashflowRows(
    row,
    r.kahunaTotal?.royalties,
    r.kahunaTotal?.projections,
    r.kahunaTotal?.advances,
    timelineActive,
    cashflowMonths,
    totalMonths,
    "Kahuna Net Cash Flow for IRR",
    false,
    advanceTotal
  )
}

function buildWideRows(results: UserAdvanceProgress[], timelineActive: boolean) {
  // Collect all unique months across all users per type
  const allRoyaltyMonths = new Set<string>()
  const allAdvanceMonths = new Set<string>()
  const allProjectionMonths = new Set<string>()

  results.forEach((r) => {
    Object.keys(r.royalties ?? {}).forEach((m) => allRoyaltyMonths.add(m))
    Object.keys(r.advances ?? {}).forEach((m) => allAdvanceMonths.add(m))
    Object.keys(r.projections ?? {}).forEach((m) => allProjectionMonths.add(m))
  })

  const royaltyMonths = Array.from(allRoyaltyMonths).sort()
  const advanceMonths = Array.from(allAdvanceMonths).sort()
  const projectionMonths = Array.from(allProjectionMonths).sort()
  const cashflowMonthList = [...royaltyMonths, ...advanceMonths, ...projectionMonths]
  const cashflowMonths = Array.from(new Set(cashflowMonthList)).sort()
  const totalMonths = cashflowMonths.length

  const rows: any = []
  results.forEach((r) => {
    let advanceTotal = 0
    Object.entries(r.advances ?? {}).forEach(([month, value]) => {
      advanceTotal += value
    })

    rows.push(getActualGrossAndProjectionRow(r, timelineActive, cashflowMonths, totalMonths))
    rows.push(getKahunaRevenueRow(r, timelineActive, cashflowMonths, totalMonths))
    rows.push(getKahunaAdvanceCollectionRow(r, timelineActive, cashflowMonths, totalMonths))
    rows.push(getArtistPayableRow(r, timelineActive, cashflowMonths, totalMonths))
    rows.push(getNewAdvanceFundedRow(r, timelineActive, cashflowMonths, totalMonths))
    rows.push(getArtistBalanceRow(r, timelineActive, cashflowMonths, totalMonths, advanceTotal))
    rows.push(getKahunaTotalRow(r, timelineActive, cashflowMonths, totalMonths, advanceTotal))
    rows.push(getKahunaBalanceRow(r, timelineActive, cashflowMonths, totalMonths))
    rows.push([])
    rows.push([])
    rows.push([])
  })

  return rows
}

export async function exportUserAdvanceProgress(
  results: UserAdvanceProgress[],
  timelineActive: boolean,
  filename = "advances_report.xlsx"
) {
  const rows = buildWideRows(results, timelineActive)

  const workbook = new ExcelJS.Workbook()
  const sheet = workbook.addWorksheet("Report")

  if (!rows.length) return

  // headers
  const headers = Object.keys(rows[0])
  sheet.addRow(headers)

  // helper for color logic
  const getFill = (val: any, key: string) => {
    const thinBorder = {
      top: { style: "hair" },
      left: { style: "hair" },
      bottom: { style: "hair" },
      right: { style: "hair" }
    }

    if (!val || !val.value || val.value === "") {
      return { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" }, border: thinBorder } // white
    }

    if (!key.startsWith("c_")) return undefined

    const num = parseFloat(val.value)

    if (isNaN(num)) return undefined

    // IMPORTANT: infer type from embedded metadata (we'll enhance below)
    const meta = val?.meta

    let fgColor = { argb: num > 0 ? "00AA00" : "FFFFFFFF" }

    if (meta?.source === "projection") {
      if (num > 0) {
        fgColor = { argb: "9fab00" }
      } else {
        fgColor = { argb: "FFA500" }
      }
    } else if (meta?.source === "default") {
      fgColor = { argb: "FFFFFFFF" }
    }

    return {
      type: "pattern",
      pattern: "solid",
      fgColor,
      border: thinBorder
    }
  }

  // rows
  rows.forEach((row) => {
    const excelRow = sheet.addRow(headers.map((h) => row[h]))

    headers.forEach((h, colIndex) => {
      const cell = excelRow.getCell(colIndex + 1)

      const val = row[h]

      if (typeof val === "object" && val !== null) {
        cell.value = val.value ?? ""

        const fill = getFill(val, h)
        if (fill) {
          cell.fill = fill
          cell.border = fill.border
        }
      }
    })
  })

  // auto width
  sheet.columns.forEach((col) => {
    col.width = 18
  })

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  })

  saveAs(blob, filename)
}
